"use client";
/* eslint-disable no-console */
import { useCallback, useMemo } from "react";
import snakeCase from "lodash/snakeCase";

import { gtagEvent } from "@stadium/services/google-analytics";

import { getIsUserBlacklisted } from "@stadium/services/analytics/helpers/blacklisted-users";

import { JUNE_SO, GTAG, HUBSPOT, MIXPANEL } from "@stadium/services/analytics/constants/services";
import { SERVER_PHASE } from "@stadium/constants";

const HUBSPOT_PORTAL_ID = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID;
const FLUSH_INTERVAL_MS = 500;
const MAX_QUEUE_AGE_MS = 30_000;

/** Set `DEBUG_ANALYTICS_EVENTS=true` to log all `trackEvent` / `pushToDataLayer` calls. */
const DEBUG_ANALYTICS_EVENTS = process.env.NEXT_PUBLIC_DEBUG_ANALYTICS_EVENTS === "true";

const emptyArr = [];

const eventQueue = new Map();
let flushTimerId = null;

const isServiceReady = (service) => {
  if (service === GTAG) return typeof globalThis.gtag === "function";
  if (service === HUBSPOT) return !!globalThis._hsq;
  if (service === MIXPANEL) return typeof globalThis.mixpanel?.track === "function";
  return false;
};

const serviceHandlers = {
  [GTAG]: ({ eventName, eventProperties }) => gtagEvent(eventName, eventProperties),
  [HUBSPOT]: ({ eventName, eventProperties }) =>
    globalThis._hsq?.push([
      "trackCustomBehavioralEvent",
      {
        name: `pe${HUBSPOT_PORTAL_ID}_${snakeCase(eventName)}`,
        properties: eventProperties,
      },
    ]),
  [MIXPANEL]: ({ eventName, eventProperties, additionalProps }) => {
    if (!getIsUserBlacklisted({ companyName: additionalProps?.companyName })) {
      return globalThis.mixpanel?.track?.(eventName, eventProperties);
    }
  },
};

const dispatchEvent = (service, event) => serviceHandlers[service]?.(event);

const dispatchPageView = (service, { eventProperties = {}, additionalProps = {} }) => {
  if (service === GTAG) {
    return gtagEvent("page_view", eventProperties);
  }
  if (service === HUBSPOT) {
    return globalThis._hsq?.push(["trackPageView"]);
  }
  if (service === MIXPANEL) {
    if (!getIsUserBlacklisted({ companyName: additionalProps?.companyName })) {
      return globalThis.mixpanel?.track_pageview?.(eventProperties);
    }
  }
};

const clearFlushTimer = () => {
  if (!flushTimerId) return;
  clearTimeout(flushTimerId);
  flushTimerId = null;
};

const hasPendingEvents = () => {
  for (const queue of eventQueue.values()) {
    if (queue.length) return true;
  }
  return false;
};

const flushServiceQueue = (service) => {
  const queue = eventQueue.get(service);
  if (!queue?.length) return;

  const now = Date.now();
  const pending = queue.filter((e) => now - e.queuedAt < MAX_QUEUE_AGE_MS);
  eventQueue.set(service, []);
  pending.forEach((e) => {
    if (e.kind === "page") {
      dispatchPageView(service, e);
    } else {
      dispatchEvent(service, e);
    }
  });

  if (!hasPendingEvents()) clearFlushTimer();
};

// scheduleFlush and attemptFlush are mutually recursive;
// scheduleFlush is defined first because its reference to attemptFlush is deferred via setTimeout.
const scheduleFlush = () => {
  if (flushTimerId) return;
  flushTimerId = setTimeout(attemptFlush, FLUSH_INTERVAL_MS);
};

const attemptFlush = () => {
  clearFlushTimer();
  const now = Date.now();

  for (const [service, queue] of eventQueue.entries()) {
    if (!queue.length) continue;
    if (isServiceReady(service)) {
      flushServiceQueue(service);
    } else {
      const fresh = queue.filter((e) => now - e.queuedAt < MAX_QUEUE_AGE_MS);
      eventQueue.set(service, fresh);
    }
  }

  if (hasPendingEvents()) scheduleFlush();
};

const enqueue = (service, event) => {
  if (!eventQueue.has(service)) eventQueue.set(service, []);
  eventQueue.get(service).push({ ...event, queuedAt: Date.now() });
  scheduleFlush();
};

/** Shared path: optional service skip, flush queue when ready, then dispatch or enqueue. */
const sendViaServiceQueue = (services, { skipService, queueItem, dispatch }) => {
  services.forEach((service) => {
    if (skipService(service)) return;
    if (isServiceReady(service)) {
      flushServiceQueue(service);
      dispatch(service, queueItem);
    } else {
      enqueue(service, queueItem);
    }
  });
};

const useActionEvents = (services = emptyArr) => {
  const trackEvent = useCallback(
    ({ eventName = "", eventProperties = {}, additionalProps = {} } = {}) => {
      if (DEBUG_ANALYTICS_EVENTS) {
        console.info("[analytics debug: trackEvent]", {
          eventName,
          eventProperties,
          additionalProps,
          services: [...services],
          serverPhase: SERVER_PHASE,
        });
      }
      sendViaServiceQueue(services, {
        skipService: (s) => SERVER_PHASE || s === JUNE_SO,
        queueItem: { eventName, eventProperties, additionalProps },
        dispatch: dispatchEvent,
      });
    },
    [services],
  );

  const trackPage = useCallback(
    (eventProperties = {}, additionalProps = {}) => {
      if (DEBUG_ANALYTICS_EVENTS) {
        console.info("[analytics debug: trackPage]", {
          eventProperties,
          additionalProps,
          services: [...services],
          serverPhase: SERVER_PHASE,
        });
      }
      sendViaServiceQueue(services, {
        skipService: (s) => SERVER_PHASE || s === JUNE_SO,
        queueItem: { kind: "page", eventProperties, additionalProps },
        dispatch: dispatchPageView,
      });
    },
    [services],
  );

  const pushToDataLayer = useCallback(({ eventName, eventProperties }) => {
    if (DEBUG_ANALYTICS_EVENTS) {
      console.info("[analytics debug: dataLayer]", {
        eventName,
        eventProperties,
        serverPhase: SERVER_PHASE,
      });
    }
    if (SERVER_PHASE || !globalThis?.dataLayer) return;
    globalThis.dataLayer.push({
      event: eventName,
      ...eventProperties,
    });
  }, []);

  return useMemo(
    () => ({ trackEvent, trackPage, pushToDataLayer }),
    [trackEvent, trackPage, pushToDataLayer],
  );
};

export default useActionEvents;
